import React from 'react';

import type { Filesystem } from '@gitbook/openapi-parser';
import {
    resolveOpenAPIOperation,
    resolveOpenAPISchemas,
    resolveOpenAPIWebhook,
} from '@gitbook/react-openapi';

export type UseResolveOpenAPIReturn<T> =
    | { error?: undefined; data: T | null; state: 'success' }
    | { error: Error; data?: undefined; state: 'error' }
    | { error?: undefined; data?: undefined; state: 'pending' };

type UseResolveOpenAPIProps = {
    filesystem: Filesystem;
} & (
    | { path: string; method: string; name?: never; schemas?: never }
    | { name: string; method: string; path?: never; schemas?: never }
    | { schemas: string[]; path?: never; method?: never; name?: never }
);

export function useResolveOpenAPI<T>(props: UseResolveOpenAPIProps): UseResolveOpenAPIReturn<T> {
    const { filesystem } = props;
    const [state, setState] = React.useState<UseResolveOpenAPIReturn<T>>({ state: 'pending' });

    // Destructure relevant props for the dependency array
    const path = 'path' in props ? props.path : undefined;
    const method = 'method' in props ? props.method : undefined;
    const name = 'name' in props ? props.name : undefined;
    const schemas = 'schemas' in props ? props.schemas : undefined;

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                let data: T | null = null;

                if (path && method) {
                    data = (await resolveOpenAPIOperation(filesystem, { path, method })) as T;
                } else if (name && method) {
                    data = (await resolveOpenAPIWebhook(filesystem, { name, method })) as T;
                } else if (schemas) {
                    data = (await resolveOpenAPISchemas(filesystem, { schemas })) as T;
                }

                if (!cancelled) {
                    setState({ data, state: 'success' });
                }
            } catch (error) {
                if (!cancelled) {
                    setState({ error: error as Error, state: 'error' });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [filesystem, path, method, name, schemas]);

    return state;
}
